<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useSeoHead } from "@/composables/use-seo-head";
import { DEFAULT_LOCALE, pathFor, type Locale } from "@/seo/routes-manifest";

const { t, tm } = useI18n();
const route = useRoute();

useSeoHead("features");

const currentLocale = computed<Locale>(
  () => (route.meta?.locale as Locale | undefined) ?? DEFAULT_LOCALE,
);
const homePath = computed(() => pathFor("home", currentLocale.value));
const philosophyPath = computed(() => pathFor("philosophy", currentLocale.value));

const heroFeatureKeys = [
  {
    id: "routine-paths",
    key: "routinePaths",
    icon: "i-lucide-map",
    reversed: false,
    image: "visual-journey-feature.webp",
  },
  {
    id: "routine-editor",
    key: "routineEditor",
    icon: "i-lucide-pen-tool",
    reversed: true,
    image: "routine-editor.webp",
  },
  {
    id: "living-themes",
    key: "livingThemes",
    icon: "i-lucide-sparkles",
    reversed: false,
    image: "growing-world.webp",
  },
  {
    id: "events-chores",
    key: "eventsChores",
    icon: "i-lucide-calendar-check",
    reversed: true,
    image: "event-calendar.webp",
  },
  {
    id: "mood-week",
    key: "moodWeek",
    icon: "i-lucide-bar-chart-3",
    reversed: false,
    image: "mood-check.webp",
  },
  {
    id: "reflections",
    key: "reflections",
    icon: "i-lucide-notebook-pen",
    reversed: true,
    image: "guided-reflections.webp",
  },
] as const;

const extraKeys = [
  { key: "languages", icon: "i-lucide-globe" },
  { key: "pinProtected", icon: "i-lucide-lock-keyhole" },
  { key: "multiChild", icon: "i-lucide-users" },
  { key: "audio", icon: "i-lucide-music" },
  { key: "taskLibrary", icon: "i-lucide-library" },
  { key: "privacy", icon: "i-lucide-shield" },
] as const;
</script>

<template>
  <article class="features-page">
    <!-- Back link -->
    <div class="px-5 pt-8 md:px-12">
      <div class="mx-auto max-w-[var(--max-w-content)]">
        <router-link
          :to="homePath"
          class="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-primary)] transition-colors hover:underline"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4" />
          {{ t("featuresPage.backToHome") }}
        </router-link>
      </div>
    </div>

    <!-- Hero -->
    <section class="px-5 pt-10 pb-12 md:px-12 md:pt-16 md:pb-20">
      <div class="mx-auto max-w-[var(--max-w-content)] text-center">
        <h1
          data-reveal
          class="fade-up text-[32px] leading-[1.15] font-medium tracking-[-0.02em] text-[var(--color-text-primary)] md:text-[48px]"
          style="font-family: var(--font-display)"
        >
          {{ t("featuresPage.heading") }}
        </h1>
        <p
          data-reveal
          data-reveal-delay="1"
          class="fade-up mx-auto mt-5 max-w-[560px] text-base leading-[1.7] text-[var(--color-text-secondary)] md:text-lg"
        >
          {{ t("featuresPage.subheading") }}
        </p>
      </div>

      <!-- Quick nav pills -->
      <div
        data-reveal
        data-reveal-delay="2"
        class="fade-up mx-auto mt-8 flex max-w-[640px] flex-wrap justify-center gap-2"
      >
        <a
          v-for="feature in heroFeatureKeys"
          :key="feature.id"
          :href="`#${feature.id}`"
          class="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
        >
          <UIcon :name="feature.icon" class="size-4" />
          {{ t(`featuresPage.${feature.key}.label`) }}
        </a>
      </div>
    </section>

    <!-- Feature sections -->
    <section
      v-for="(feature, index) in heroFeatureKeys"
      :id="feature.id"
      :key="feature.id"
      class="px-5 py-12 md:px-12 md:py-24"
      :class="index % 2 === 0 ? 'bg-[var(--color-surface)]' : 'bg-[var(--color-bg-secondary)]'"
    >
      <div
        class="mx-auto grid max-w-[var(--max-w-page)] items-center gap-10 md:grid-cols-2 md:gap-16"
        :class="feature.reversed ? 'md:[direction:rtl]' : ''"
      >
        <!-- Text -->
        <div :class="feature.reversed ? 'md:[direction:ltr]' : ''">
          <div
            data-reveal
            class="fade-up inline-flex items-center gap-2 rounded-full bg-[var(--color-primary-light)] px-3 py-1"
          >
            <UIcon :name="feature.icon" class="size-4 text-[var(--color-primary)]" />
            <span class="text-xs font-semibold tracking-wide text-[var(--color-primary)] uppercase">
              {{ t(`featuresPage.${feature.key}.label`) }}
            </span>
          </div>

          <h2
            data-reveal
            data-reveal-delay="1"
            class="fade-up mt-5 text-2xl font-medium text-[var(--color-text-primary)] md:text-[32px] md:leading-[1.2]"
            style="font-family: var(--font-display)"
          >
            {{ t(`featuresPage.${feature.key}.title`) }}
          </h2>

          <p
            data-reveal
            data-reveal-delay="2"
            class="fade-up mt-4 text-base leading-[1.7] text-[var(--color-text-secondary)] md:text-lg"
          >
            {{ t(`featuresPage.${feature.key}.lead`) }}
          </p>

          <ul class="mt-6 space-y-3">
            <li
              v-for="(_, i) in tm(`featuresPage.${feature.key}.details`) as string[]"
              :key="i"
              data-reveal
              :data-reveal-delay="3 + i"
              class="fade-up flex items-start gap-3 text-sm leading-[1.6] text-[var(--color-text-secondary)] md:text-base"
            >
              <UIcon
                name="i-lucide-check"
                class="mt-0.5 size-4 shrink-0 text-[var(--color-primary)]"
              />
              {{ t(`featuresPage.${feature.key}.details[${i}]`) }}
            </li>
          </ul>
        </div>

        <!-- Mockup -->
        <div :class="feature.reversed ? 'md:[direction:ltr]' : ''">
          <img
            data-reveal
            :data-reveal-delay="2"
            :src="`/screenshots/${feature.image}`"
            :alt="t(`featuresPage.${feature.key}.mockupAlt`)"
            width="1600"
            height="1200"
            class="fade-up block aspect-[4/3] w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-primary)] object-cover shadow-sm"
            loading="lazy"
            decoding="async"
            draggable="false"
          />
        </div>
      </div>
    </section>

    <!-- Also included -->
    <section class="bg-[var(--color-bg-primary)] px-5 py-12 md:px-12 md:py-24">
      <div class="mx-auto max-w-[var(--max-w-page)]">
        <h2
          data-reveal
          class="fade-up mb-10 text-center text-2xl font-medium text-[var(--color-text-primary)] md:mb-14 md:text-[32px]"
          style="font-family: var(--font-display)"
        >
          {{ t("featuresPage.alsoIncluded") }}
        </h2>

        <div class="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
          <div
            v-for="(item, i) in extraKeys"
            :key="item.key"
            data-reveal
            :data-reveal-delay="i % 3"
            class="fade-up rounded-[14px] bg-[var(--color-bg-secondary)] p-5 md:p-6"
          >
            <UIcon :name="item.icon" class="size-8 text-[var(--color-primary)]" />
            <h3
              class="mt-3 text-sm font-medium text-[var(--color-text-primary)] md:text-base"
              style="font-family: var(--font-display)"
            >
              {{ t(`featuresPage.extras.${item.key}.title`) }}
            </h3>
            <p class="mt-1 text-xs leading-relaxed text-[var(--color-text-secondary)] md:text-sm">
              {{ t(`featuresPage.extras.${item.key}.body`) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="bg-[var(--color-surface)] px-5 py-12 md:px-12 md:py-24">
      <div class="mx-auto max-w-[var(--max-w-content)] text-center">
        <h2
          data-reveal
          class="fade-up text-2xl font-medium text-[var(--color-text-primary)] md:text-[32px]"
          style="font-family: var(--font-display)"
        >
          {{ t("featuresPage.readyToTry") }}
        </h2>
        <p
          data-reveal
          data-reveal-delay="1"
          class="fade-up mx-auto mt-4 max-w-[480px] text-base leading-[1.7] text-[var(--color-text-secondary)]"
        >
          {{ t("featuresPage.readySubheading") }}
        </p>
        <div
          data-reveal
          data-reveal-delay="2"
          class="fade-up mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
        >
          <router-link
            :to="{ path: homePath, hash: '#hero' }"
            class="inline-flex items-center rounded-full bg-[var(--color-primary)] px-8 py-3 text-base font-semibold text-white transition-colors hover:bg-[var(--color-primary-hover)]"
          >
            {{ t("featuresPage.getStarted") }}
          </router-link>
          <router-link
            :to="philosophyPath"
            class="inline-flex items-center gap-1.5 text-[15px] font-medium text-[var(--color-primary)] transition-colors hover:underline"
          >
            {{ t("featuresPage.ourPhilosophy") }}
            <UIcon name="i-lucide-arrow-right" class="size-4" />
          </router-link>
        </div>
      </div>
    </section>
  </article>
</template>
